import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { Card, Overline, BtnSecondary, Tag } from "@/components/cathedral/Primitives";

// Cathedral neon palette for up to 12 lenses
const PALETTE = [
  "#00F0FF", "#FF2BD6", "#8B2CFF", "#FFB200", "#2BFF8A",
  "#FF3B5C", "#3DA8FF", "#C0C7D8", "#FF7A1A", "#9DFF66",
  "#FF66E0", "#66E5FF",
];

export default function LensDriftChart() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState({}); // lens → bool

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const d = await api.lensDrift(50);
      setData(d);
      // Default: show top 6 most-confident lenses for legibility
      const top = Object.entries(d.averages || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([k]) => k);
      const init = {};
      (d.lenses || []).forEach((l) => { init[l] = top.includes(l); });
      setVisible(init);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Pivot: [{ts, "Mind": .., "Heart": ..., consensus: ...}, ...]
  const chartRows = useMemo(() => {
    if (!data) return [];
    return data.series.map((row, i) => {
      const out = { ts: row.timestamp ? row.timestamp.slice(11, 19) : `#${i + 1}` };
      Object.entries(row.lens_confidences || {}).forEach(([k, v]) => {
        out[k] = Math.round((v || 0) * 100);
      });
      const cons = data.consensus_series?.[i]?.consensus;
      out.consensus = cons != null ? Math.round(cons * 100) : null;
      return out;
    });
  }, [data]);

  return (
    <Card className="p-7" data-testid="lens-drift-chart">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0">
          <Overline tone="violet">Lens Drift · Ring of Twelve · Over Time</Overline>
          <h3 className="font-display text-2xl font-light tracking-[0.04em] mt-3 leading-none text-glow-violet">
            CONFIDENCE TRAJECTORY
          </h3>
          <p className="text-sm text-[var(--text-2)] leading-relaxed mt-4 max-w-2xl">
            Per-lens confidence across the most recent {data?.count ?? "—"} deep-mode Ring deliberations. Use this to spot drift — any lens consistently below consensus is the one to audit (DIR-018 Mirror Audit).
          </p>
        </div>
        <BtnSecondary onClick={load} disabled={busy} data-testid="lens-drift-refresh">
          {busy ? "Loading…" : "Refresh"}
        </BtnSecondary>
      </div>

      {err && (
        <div className="font-mono text-xs text-glow-red mb-4">{err}</div>
      )}

      {!data ? (
        <div className="font-mono text-xs text-[var(--text-3)] py-10 text-center">Loading drift data…</div>
      ) : chartRows.length === 0 ? (
        <div className="font-mono text-xs text-[var(--text-3)] py-10 text-center">
          No deep-mode deliberations recorded yet. Run /execute with <code className="text-glow-cyan">mode=deep</code> a few times to seed the drift series.
        </div>
      ) : (
        <>
          {/* Legend toggles */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(data.lenses || []).map((lens, i) => {
              const color = PALETTE[i % PALETTE.length];
              const on = !!visible[lens];
              const avg = data.averages?.[lens];
              return (
                <button
                  key={lens}
                  onClick={() => setVisible({ ...visible, [lens]: !on })}
                  data-testid={`lens-toggle-${lens.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2.5 py-1.5 border transition-all flex items-center gap-2 ${
                    on ? "border-[var(--chrome-2)] text-[var(--text)]" : "border-[var(--chrome)] text-[var(--text-4)] opacity-50"
                  }`}
                  style={on ? { boxShadow: `0 0 8px ${color}66` } : {}}
                >
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: color, boxShadow: on ? `0 0 8px ${color}` : "none" }} />
                  <span>{lens}</span>
                  {avg != null && (
                    <span className="text-[var(--text-3)]">·{Math.round(avg * 100)}%</span>
                  )}
                </button>
              );
            })}
            <Tag tone="cite">consensus (white)</Tag>
          </div>

          <div className="h-80 -mx-2 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <LineChart data={chartRows} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="ts" tick={{ fontSize: 10, fill: "#7c849b", fontFamily: "JetBrains Mono, monospace" }} stroke="rgba(255,255,255,0.1)" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#7c849b", fontFamily: "JetBrains Mono, monospace" }} stroke="rgba(255,255,255,0.1)" />
                <Tooltip
                  contentStyle={{ background: "#07081a", border: "1px solid #1f2540", fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}
                  labelStyle={{ color: "#c0c7d8" }}
                  itemStyle={{ color: "#e6e9f2" }}
                />
                <Legend wrapperStyle={{ display: "none" }} />
                {(data.lenses || []).map((lens, i) => visible[lens] && (
                  <Line
                    key={lens}
                    type="monotone"
                    dataKey={lens}
                    stroke={PALETTE[i % PALETTE.length]}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="consensus"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}
